import type { FC, FocusEvent, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { NavIcon, NavItem } from '@/constants/navigation'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/generals/logo'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { docsGroups, productLinks, resourceLinks } from '@/constants/navigation'

import {
    AppWindow,
    Braces,
    ChevronDown,
    Handshake,
    History,
    Newspaper,
    Sparkles,
    Terminal,
    Workflow
} from 'lucide-react'

type MenuId = 'products' | 'resources' | 'docs'

const navIcons: Record<NavIcon, LucideIcon> = {
    'full-service': Handshake,
    'studio': Workflow,
    'pilot': Sparkles,
    'one-api': Braces,
    'blog': Newspaper,
    'changelog': History,
    'embedded': AppWindow,
    'platform-api': Terminal
}

const navItemClasses = 'inline-flex h-9 items-center gap-1 rounded-full px-3.5 text-[14px] font-medium tracking-[-0.01em] text-graphite transition-colors hover:bg-mist hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet'

interface MenuItemProps {
    item: NavItem
    onSelect: () => void
}

const MenuItem: FC<MenuItemProps> = ({ item, onSelect }): ReactNode => {
    const { openContact } = useContact()
    const Icon = navIcons[item.icon]

    const content = (
        <>
            <span className='flex size-11 shrink-0 items-center justify-center rounded-[12px] bg-white ring-1 ring-hairline transition-colors duration-200 group-hover:bg-violet-soft group-hover:ring-[#e3d9ff] group-focus-visible:bg-violet-soft'>
                <Icon
                    aria-hidden='true'
                    strokeWidth={1.6}
                    className='size-5 text-slate transition-colors duration-200 group-hover:fill-[#ddd0ff] group-hover:text-violet group-focus-visible:fill-[#ddd0ff] group-focus-visible:text-violet'
                />
            </span>

            <span className='flex min-w-0 flex-col gap-0.5'>
                <span className='text-[14px] font-semibold tracking-[-0.01em] text-ink'>
                    {item.title}
                </span>

                <span className='text-[13px] leading-snug text-slate'>
                    {item.description}
                </span>
            </span>
        </>
    )

    const classes = 'group flex w-full items-center gap-3.5 rounded-[14px] p-2.5 pr-4 text-left transition-colors duration-200 hover:bg-mist focus-visible:outline-2 focus-visible:outline-violet'

    if (item.href) {
        return (
            <Link href={item.href} onClick={onSelect} className={classes}>
                {content}
            </Link>
        )
    }

    return (
        <button
            type='button'
            className={classes}
            onClick={() => {
                onSelect()
                openContact({ intent: item.intent })
            }}
        >
            {content}
        </button>
    )
}

interface DropdownProps {
    id: MenuId
    label: string
    openMenu: MenuId | null
    setOpenMenu: (menu: MenuId | null) => void
    panelClassName?: string
    children: ReactNode
}

const Dropdown: FC<DropdownProps> = ({ id, label, openMenu, setOpenMenu, panelClassName, children }): ReactNode => {
    const isOpen = openMenu === id
    const closeTimer = useRef<number | null>(null)

    const cancelClose = () => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current)
    }

    const scheduleClose = () => {
        cancelClose()
        closeTimer.current = window.setTimeout(() => setOpenMenu(null), 140)
    }

    useEffect(() => () => cancelClose(), [])

    const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpenMenu(null)
    }

    return (
        <div
            className='relative'
            onMouseEnter={() => {
                cancelClose()
                setOpenMenu(id)
            }}
            onMouseLeave={scheduleClose}
            onBlur={handleBlur}
            onKeyDown={event => {
                if (event.key === 'Escape') setOpenMenu(null)
            }}
        >
            <button
                type='button'
                aria-expanded={isOpen}
                aria-controls={`${id}-menu`}
                onClick={() => setOpenMenu(isOpen ? null : id)}
                className={cn(navItemClasses, isOpen && 'bg-mist text-ink')}
            >
                {label}

                <ChevronDown
                    aria-hidden='true'
                    className={cn('size-3.5 text-silver transition-transform duration-200', isOpen && 'rotate-180')}
                />
            </button>

            <div
                id={`${id}-menu`}
                className={cn(
                    'absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 transition-[opacity,translate,visibility] duration-200 ease-out',
                    isOpen ? 'visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-1.5 opacity-0'
                )}
            >
                <div className={cn('rounded-[22px] bg-white p-2 shadow-[0_24px_60px_-28px_rgba(14,14,14,0.35),0_8px_20px_-12px_rgba(14,14,14,0.12)] ring-1 ring-[#e9e9ee]', panelClassName)}>
                    {children}
                </div>
            </div>
        </div>
    )
}

interface MobileMenuProps {
    onClose: () => void
}

const MobileMenu: FC<MobileMenuProps> = ({ onClose }): ReactNode => {
    const { openContact } = useContact()
    const [expanded, setExpanded] = useState<MenuId | null>(null)
    const closeRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        const previousOverflow = document.body.style.overflow
        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }

        document.body.style.overflow = 'hidden'
        document.addEventListener('keydown', handleKey)
        closeRef.current?.focus()

        return () => {
            document.body.style.overflow = previousOverflow
            document.removeEventListener('keydown', handleKey)
        }
    }, [onClose])

    const section = (id: MenuId, label: string, body: ReactNode) => (
        <div className='border-b border-hairline'>
            <button
                type='button'
                aria-expanded={expanded === id}
                aria-controls={`mobile-${id}`}
                onClick={() => setExpanded(expanded === id ? null : id)}
                className='flex w-full items-center justify-between py-4 text-left text-[22px] font-medium tracking-[-0.03em] text-ink'
            >
                {label}

                <ChevronDown aria-hidden='true' className={cn('size-5 text-silver transition-transform duration-300', expanded === id && 'rotate-180')} />
            </button>

            <div
                id={`mobile-${id}`}
                className={cn('grid transition-[grid-template-rows,opacity] duration-300 ease-out', expanded === id ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0')}
            >
                <div className='overflow-hidden' inert={expanded !== id}>
                    <div className='flex flex-col gap-1 pb-4'>
                        {body}
                    </div>
                </div>
            </div>
        </div>
    )

    // Rendered into the body: the blurred header would otherwise trap this fixed overlay inside its own box.
    return createPortal(
        <div id='mobile-navigation' role='dialog' aria-modal='true' aria-label='Navigation' className='fc-fade-up fixed inset-0 z-[90] flex flex-col bg-white lg:hidden'>
            <div className='fc-container flex h-16 shrink-0 items-center justify-between'>
                <Link href='/' onClick={onClose} aria-label='FlowCraft home'>
                    <Logo />
                </Link>

                <button
                    ref={closeRef}
                    type='button'
                    onClick={onClose}
                    aria-label='Close navigation menu'
                    className='relative inline-flex size-10 items-center justify-center rounded-full hover:bg-mist focus-visible:outline-2 focus-visible:outline-violet'
                >
                    <span className='absolute h-[1.5px] w-5 rotate-45 rounded-full bg-ink' />
                    <span className='absolute h-[1.5px] w-5 -rotate-45 rounded-full bg-ink' />
                </button>
            </div>

            <div className='fc-container flex flex-1 flex-col overflow-y-auto pb-8 pt-2'>
                <p className='pb-2 text-[13px] font-medium text-silver'>
                    Navigation
                </p>

                <nav aria-label='Mobile' className='flex flex-col'>
                    {section('products', 'Products', productLinks.map(item => (
                        <MenuItem key={item.title} item={item} onSelect={onClose} />
                    )))}

                    <Link href='/apps' onClick={onClose} className='border-b border-hairline py-4 text-[22px] font-medium tracking-[-0.03em] text-ink'>
                        Apps
                    </Link>

                    {section('resources', 'Resources', resourceLinks.map(item => (
                        <MenuItem key={item.title} item={item} onSelect={onClose} />
                    )))}

                    {section('docs', 'Docs', docsGroups.map(group => (
                        <div key={group.label} className='flex flex-col gap-1 pt-1'>
                            <p className='px-2.5 pb-1 pt-2 text-[12px] font-medium uppercase tracking-[0.08em] text-silver'>
                                {group.label}
                            </p>

                            {group.items.map(item => (
                                <MenuItem key={item.title} item={item} onSelect={onClose} />
                            ))}
                        </div>
                    )))}
                </nav>

                <div className='mt-auto flex flex-col gap-3 pt-10'>
                    <PillButton
                        variant='secondary'
                        size='lg'
                        className='w-full'
                        onClick={() => {
                            onClose()
                            openContact({ intent: 'access' })
                        }}
                    >
                        Sign in
                    </PillButton>

                    <PillButton
                        size='lg'
                        chevron
                        className='w-full'
                        onClick={() => {
                            onClose()
                            openContact({ intent: 'start' })
                        }}
                    >
                        Get started
                    </PillButton>
                </div>
            </div>
        </div>,
        document.body
    )
}

export const Header: FC = (): ReactNode => {
    const router = useRouter()
    const { openContact } = useContact()
    const [scrolled, setScrolled] = useState(false)
    const [openMenu, setOpenMenu] = useState<MenuId | null>(null)
    const [mobileOpen, setMobileOpen] = useState(false)

    useEffect(() => {
        const update = () => setScrolled(window.scrollY > 8)

        update()
        window.addEventListener('scroll', update, { passive: true })

        return () => window.removeEventListener('scroll', update)
    }, [])

    useEffect(() => {
        const reset = () => {
            setOpenMenu(null)
            setMobileOpen(false)
        }

        router.events.on('routeChangeStart', reset)
        router.events.on('hashChangeStart', reset)

        return () => {
            router.events.off('routeChangeStart', reset)
            router.events.off('hashChangeStart', reset)
        }
    }, [router.events])

    const closeMenus = () => setOpenMenu(null)
    const closeMobile = useCallback(() => setMobileOpen(false), [])

    return (
        <header
            className={cn(
                'sticky top-0 z-50 border-b backdrop-blur-xl backdrop-saturate-150 transition-[background-color,border-color] duration-300',
                scrolled ? 'border-hairline bg-white/85' : 'border-transparent bg-white/55'
            )}
        >
            <div className='fc-container grid h-16 grid-cols-[1fr_auto_1fr] items-center gap-4'>
                <Link href='/' aria-label='FlowCraft home' className='justify-self-start rounded-md focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-violet'>
                    <Logo />
                </Link>

                <nav aria-label='Primary' className='hidden items-center gap-0.5 lg:flex'>
                    <Dropdown id='products' label='Products' openMenu={openMenu} setOpenMenu={setOpenMenu} panelClassName='w-[400px]'>
                        <div className='flex flex-col'>
                            {productLinks.map(item => (
                                <MenuItem key={item.title} item={item} onSelect={closeMenus} />
                            ))}
                        </div>
                    </Dropdown>

                    <Link
                        href='/apps'
                        aria-current={router.pathname === '/apps' ? 'page' : undefined}
                        className={cn(navItemClasses, router.pathname === '/apps' && 'text-ink')}
                    >
                        Apps
                    </Link>

                    <Dropdown id='resources' label='Resources' openMenu={openMenu} setOpenMenu={setOpenMenu} panelClassName='w-[380px]'>
                        <div className='flex flex-col'>
                            {resourceLinks.map(item => (
                                <MenuItem key={item.title} item={item} onSelect={closeMenus} />
                            ))}
                        </div>
                    </Dropdown>

                    <Dropdown id='docs' label='Docs' openMenu={openMenu} setOpenMenu={setOpenMenu} panelClassName='grid w-[700px] grid-cols-[1.12fr_1fr] gap-2'>
                        {docsGroups.map((group, index) => (
                            <div key={group.label} className={cn('flex flex-col gap-1 rounded-[16px] p-1', index === 1 && 'bg-mist/70')}>
                                <p className='px-2.5 pb-1 pt-2.5 text-[12px] font-medium uppercase tracking-[0.08em] text-silver'>
                                    {group.label}
                                </p>

                                {group.items.map(item => (
                                    <MenuItem key={item.title} item={item} onSelect={closeMenus} />
                                ))}
                            </div>
                        ))}
                    </Dropdown>
                </nav>

                <div className='col-start-3 flex items-center gap-1.5 justify-self-end sm:gap-2'>
                    <button
                        type='button'
                        onClick={() => openContact({ intent: 'access' })}
                        className={cn(navItemClasses, 'hidden lg:inline-flex')}
                    >
                        Sign in
                    </button>

                    <PillButton size='sm' chevron className='hidden sm:inline-flex' onClick={() => openContact({ intent: 'start' })}>
                        Get started
                    </PillButton>

                    <button
                        type='button'
                        onClick={() => setMobileOpen(true)}
                        aria-label='Open navigation menu'
                        aria-expanded={mobileOpen}
                        aria-controls='mobile-navigation'
                        className='relative inline-flex size-10 items-center justify-center rounded-full hover:bg-mist focus-visible:outline-2 focus-visible:outline-violet lg:hidden'
                    >
                        <span className='absolute h-[1.5px] w-5 -translate-y-[3.5px] rounded-full bg-ink' />
                        <span className='absolute h-[1.5px] w-5 translate-y-[3.5px] rounded-full bg-ink' />
                    </button>
                </div>
            </div>

            {mobileOpen && <MobileMenu onClose={closeMobile} />}
        </header>
    )
}
